// The Schedules list as a calm list (pure; scheduleListModel.selftest.ts runs
// it): each row's title in words (the slug stays in its details), one short
// line of when it runs, who does it when that is not its own agent, and how
// you hear back; the rows in the order a person reads them (what needs you,
// then what runs next, then what is paused); ended one-offs apart, folded;
// the platform jobs grouped by kind; a row's details say only what the row
// does not (detailWhat, detailFacts, lastRunFact).

import type { PlatformRoutine, ScheduleRun, UnifiedSchedule } from "@/api/schedulesUnified";
import { deliveryWords, isTaskStatus, taskStatusWord } from "@/features/tasks/taskModel";
import { browserZone } from "@/features/workflows/editor/scheduleModel";
import { humanizeSlug, pluralize, pluralWord } from "@/lib/format";
import { routineDisplayName } from "@/lib/routineNames";

import { afterOf, chainStateLine, placeholderWords } from "./chainModel";
import { lastWord, whenText, type RowWord } from "./scheduleWords";

type NameOf = (template: string) => string;

/** A slug an agent or a migration chose ("remind-call-emma"), not a name a person typed. */
const SLUG = /^[a-z0-9]+(?:[-_][a-z0-9]+)*$/;
/** A reminder's line longer than this is a poor title: the name in words instead. */
const MAX_LINE_TITLE = 90;

/**
 * The row's title: a reminder named by a slug shows the line it sends
 * ("Send the codebase to Davide (final reminder)"), anything else its name
 * in words ("inbox-watch" → "Inbox watch", "lattice-checkin" → "Check-in").
 * Display only: the real name goes back in every write.
 */
export function scheduleTitle(row: Pick<UnifiedSchedule, "name" | "who" | "what">): string {
  if (titledByLine(row)) {
    const line = reminderLine(row);
    return line.charAt(0).toUpperCase() + line.slice(1);
  }
  return routineDisplayName(row.name, row.what.brief) || row.name;
}

/** A reminder's line on one line ("" for anything else). */
function reminderLine(row: Pick<UnifiedSchedule, "who" | "what">): string {
  return row.who.kind === "message" ? (row.what.brief ?? "").replace(/\s+/g, " ").trim() : "";
}

/** The title IS the reminder's line (a slug name, a short line): its details need not say it again. */
export function titledByLine(row: Pick<UnifiedSchedule, "name" | "who" | "what">): boolean {
  const line = reminderLine(row);
  return !!line && line.length <= MAX_LINE_TITLE && SLUG.test(row.name);
}

const CHANNEL: Readonly<Record<string, string>> = { telegram: "Telegram", whatsapp: "WhatsApp" };

/** How you hear back, short: "on WhatsApp", "in chat", "by email", "quiet"; "" when the worker's own settings say. */
export function hearWords(row: Pick<UnifiedSchedule, "who" | "report">): string {
  if (row.who.kind === "team" || row.who.kind === "workflow") return "";
  const rep = row.report;
  if (!rep || rep.mode === "quiet") return "quiet";
  if (rep.mode === "email") return "by email";
  const where = CHANNEL[rep.channel] ? `on ${CHANNEL[rep.channel]}` : "in chat";
  return row.who.kind === "message" ? `reminder ${where}` : where;
}

/** Who does it, only when that is not the row's own agent: "by Ray Dalio", "by the team", "the Research desk workflow". */
export function whoWords(row: Pick<UnifiedSchedule, "who" | "owner">, nameOf: NameOf): string {
  const who = row.who;
  switch (who.kind) {
    case "agent":
    case "subagent":
      return who.ref && who.ref !== row.owner ? `by ${nameOf(who.ref)}` : "";
    case "team":
      return "by the team";
    case "workflow":
      return `the ${humanizeSlug(who.ref) || "saved"} workflow`;
    default:
      return "";
  }
}

/**
 * The one line under the title: "Weekdays at 09:30 and 14:30 · on WhatsApp",
 * "Once · reminder on Telegram"; an ended one says what it did: "Sent on
 * Telegram", "Ran once · on WhatsApp" (the time is beside it).
 */
export function rowLine(row: Pick<UnifiedSchedule, "when" | "who" | "owner" | "report" | "state">, nameOf: NameOf, browser = browserZone()): string {
  const hear = hearWords(row);
  if (isEnded(row) && row.who.kind === "message") return hear.replace(/^reminder /, "Sent ");
  const when = row.when.cron || row.when.every_minutes || afterOf(row) ? whenText(row, undefined, browser) : isEnded(row) ? "Ran once" : "Once";
  return [when, whoWords(row, nameOf), hear].filter(Boolean).join(" · ");
}

/** A workflow's inputs in words: "symbols: BTC, ETH · days: 30" ("" when none). */
export function paramsWords(params: Record<string, unknown> | null | undefined): string {
  const words = Object.entries(params ?? {}).map(([k, v]) => {
    const value = Array.isArray(v) ? v.map(String).join(", ") : v && typeof v === "object" ? JSON.stringify(v) : String(v ?? "");
    return value ? `${humanizeSlug(k).toLowerCase()}: ${value}` : "";
  });
  return words.filter(Boolean).join(" · ");
}

/** Ended: a one-off that has run (or will not run again); it goes in the fold. */
export function isEnded(row: Pick<UnifiedSchedule, "state">): boolean {
  return row.state === "ended";
}

/** Needs the operator: waits for an OK, is held, or its last slot was skipped. */
export function needsYou(row: Pick<UnifiedSchedule, "state" | "last_error">): boolean {
  return row.state === "awaiting_approval" || row.state === "held" || !!row.last_error;
}

type Orderable = Pick<UnifiedSchedule, "name" | "who" | "what" | "state" | "last_error" | "next_run_at" | "last_run_at">;

function rank(r: Orderable): number {
  if (needsYou(r)) return 0;
  if (isEnded(r)) return 3;
  if (r.state === "paused") return 2;
  return 1;
}

/** An instant in ms; a missing or unreadable one sorts as `missing`. */
const time = (iso: string | null | undefined, missing: number): number => {
  const t = iso ? Date.parse(iso) : Number.NaN;
  return Number.isFinite(t) ? t : missing;
};

/**
 * The order a person reads them in: what needs you, then what runs next
 * (soonest first), then what is paused; ended ones newest first. Ties by title.
 */
export function orderRows<T extends Orderable>(rows: readonly T[]): T[] {
  return [...rows].sort((a, b) => {
    const ra = rank(a);
    const rb = rank(b);
    if (ra !== rb) return ra - rb;
    if (ra === 1) {
      const [ta, tb] = [time(a.next_run_at, Number.POSITIVE_INFINITY), time(b.next_run_at, Number.POSITIVE_INFINITY)];
      if (ta !== tb) return ta < tb ? -1 : 1;
    }
    if (ra === 3) {
      const [ta, tb] = [time(a.last_run_at, Number.NEGATIVE_INFINITY), time(b.last_run_at, Number.NEGATIVE_INFINITY)];
      if (ta !== tb) return ta > tb ? -1 : 1;
    }
    return scheduleTitle(a).localeCompare(scheduleTitle(b));
  });
}

/** The ended fold's label: "Ended reminders" when every row in it is a reminder, else "Ended". */
export function endedLabel(rows: readonly Pick<UnifiedSchedule, "who">[]): string {
  return rows.length > 0 && rows.every((r) => r.who.kind === "message") ? "Ended reminders" : "Ended";
}

/**
 * "7 schedules, 1 paused": the short count for a filtered list (an agent's
 * tab). The ended ones are counted on their own fold; "" for an empty list
 * (its empty state says so).
 */
export function countLine(rows: readonly Pick<UnifiedSchedule, "state">[]): string {
  if (rows.length === 0) return "";
  const live = rows.filter((r) => !isEnded(r));
  if (live.length === 0) return "Nothing on a timetable now.";
  const paused = live.filter((r) => r.state === "paused").length;
  return [pluralize(live.length, "schedule"), paused ? `${paused} paused` : ""].filter(Boolean).join(", ");
}

// ─── the details ───

export interface DetailFact {
  label: string;
  text: string;
}

/**
 * The first thing the details say: what it does (the brief), the line a
 * reminder sends when its title is not already that line, a workflow's
 * inputs. null: the title says it all (a reminder titled by its line).
 * An empty `text` is a brief nobody wrote.
 */
export function detailWhat(row: Pick<UnifiedSchedule, "name" | "who" | "what">): DetailFact | null {
  if (row.who.kind === "workflow") return { label: "Inputs", text: placeholderWords(paramsWords(row.what.params)) || "Its usual inputs" };
  if (row.who.kind === "message") return titledByLine(row) ? null : { label: "Sends", text: reminderLine(row) ? `“${reminderLine(row)}”` : "" };
  return { label: "What it does", text: (row.what.brief ?? "").trim() };
}

/**
 * How you hear back, in the details, only when the row's short word does not
 * say it all: quiet, email (to whom), a team or a workflow (their own way).
 * "" for an answer or a line in chat (the row says where).
 */
export function hearDetail(row: Pick<UnifiedSchedule, "who" | "report">): string {
  if (row.who.kind === "team") return "As a task, on the Tasks page";
  if (row.who.kind === "workflow") return "By the workflow's own settings";
  const rep = row.report;
  if (!rep || rep.mode === "quiet") return "Nothing is sent: each run is on the Tasks page";
  if (rep.mode === "email") return (rep.email_to ?? []).length ? `By email to ${(rep.email_to ?? []).join(", ")}` : "By email";
  return "";
}

/** The time of the row's column on the right: the next run, or when an ended one ran (null: nothing there). */
export function timeBeside(row: Pick<UnifiedSchedule, "state" | "active" | "next_run_at" | "last_run_at">): string | null {
  if (isEnded(row)) return row.last_run_at || null;
  return row.active && row.next_run_at ? row.next_run_at : null;
}

/**
 * The details' facts the row does not already show (its line says when,
 * who and where; the column on the right the next run): a one-off's time
 * when nothing is beside it (paused, held, waiting), and how you hear back
 * when the row's word needs saying out (./hearDetail).
 */
export function detailFacts(row: Pick<UnifiedSchedule, "when" | "who" | "report" | "state" | "active" | "next_run_at" | "last_run_at"> & Partial<Pick<UnifiedSchedule, "chain">>, browser = browserZone()): DetailFact[] {
  const facts: DetailFact[] = [];
  const oneOff = !row.when.cron && !row.when.every_minutes && !afterOf(row);
  const chain = afterOf(row) ? chainStateLine(row.chain) : "";
  if (chain) facts.push({ label: "Recently", text: chain });
  if (oneOff && row.when.at && !timeBeside(row)) facts.push({ label: "When", text: whenText(row, undefined, browser) });
  const hear = hearDetail(row);
  if (hear) facts.push({ label: "You hear", text: hear });
  return facts;
}

/**
 * The details' last run: its time (unless it is already beside the row, as
 * for an ended one) and how it went only when that is not the usual (done,
 * sent). null: nothing to add (never run, or an ended one that went fine).
 */
export function lastRunFact(row: Pick<UnifiedSchedule, "who" | "state" | "last_run_at" | "last_status" | "last_error">): { at: string | null; word: RowWord | null } | null {
  const w = lastWord(row);
  const word = w && w.tone !== "success" ? w : null;
  const at = !isEnded(row) && row.last_run_at ? row.last_run_at : null;
  return at || word ? { at, word } : null;
}

export interface PlatformKind {
  /** "Heartbeats", "Inbox watchers", "Skill collection reviews". */
  label: string;
  rows: PlatformRoutine[];
  on: number;
}

/** The platform jobs by kind (the name in words), biggest kind first; in each, the ones on first. */
export function platformKinds(jobs: readonly PlatformRoutine[], nameOf: NameOf): PlatformKind[] {
  const by = new Map<string, PlatformRoutine[]>();
  for (const j of jobs) {
    const kind = routineDisplayName(j.name) || j.name;
    by.set(kind, [...(by.get(kind) ?? []), j]);
  }
  return [...by.entries()]
    .map(([kind, rows]) => ({
      label: pluralWord(kind, rows.length),
      rows: [...rows].sort((a, b) => Number(!!b.enabled) - Number(!!a.enabled) || nameOf(a.agent_template).localeCompare(nameOf(b.agent_template))),
      on: rows.filter((r) => !!r.enabled).length,
    }))
    .sort((a, b) => b.rows.length - a.rows.length || a.label.localeCompare(b.label));
}

/** A kind's switch state in words: "all on", "6 on, 27 off", "off". */
export function onOffWords(on: number, total: number): string {
  if (on === total) return total === 1 ? "on" : "all on";
  if (on === 0) return total === 1 ? "off" : "all off";
  return `${on} on, ${total - on} off`;
}

export interface RunWords {
  /** Only what is not the usual (done, sent): "Failed", "Not delivered — resend". */
  badges: RowWord[];
  /** A quiet note: "Answer sent", "Sent", "Nothing to say" (a NO_REPLY run), else what went wrong. */
  note: string;
}

/** One History line in words: the usual (done and delivered, a reminder sent) says nothing. */
export function runWords(run: Pick<ScheduleRun, "kind" | "status" | "delivery" | "summary">): RunWords {
  const badges: RowWord[] = [];
  if (run.kind === "task") {
    if (run.status !== "finished") {
      const w = isTaskStatus(run.status) ? taskStatusWord(run.status) : null;
      if (w) badges.push({ label: w.label, tone: w.tone });
    }
    const d = run.delivery === "delivered" ? null : deliveryWords(run.delivery);
    if (d) badges.push(d);
  } else {
    const w = lastWord({ who: { kind: "message", ref: "" }, last_status: run.status ?? null, last_error: null });
    if (w && w.tone !== "success") badges.push(w);
  }
  const summary = (run.summary ?? "").trim();
  if (/\bNO_REPLY\b/.test(summary)) return { badges, note: "Nothing to say" };
  if (summary || badges.length) return { badges, note: summary };
  return { badges, note: run.kind === "task" ? (run.delivery === "delivered" ? "Answer sent" : "") : "Sent" };
}
